import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import { convexQuery } from '@convex-dev/react-query'
import { api } from 'convex/_generated/api'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { Id } from 'convex/_generated/dataModel'
import { AssetDetailPage } from '@/features/assets/components/AssetDetailPage'

export const Route = createFileRoute('/_authed/assets/$id')({
  loader: async ({ params, context }) => {
    const { id } = params
    
    // Validate params
    if (!id || typeof id !== 'string') {
      throw notFound()
    }
    
    // Preload critical data - ensures data is available before render
    const asset = await context.queryClient.ensureQueryData(
      convexQuery(api.assets.getAsset, { id: id as Id<'assetInventory'> })
    )
    
    // Throw notFound if resource doesn't exist
    if (!asset) {
      throw notFound()
    }
    
    return { assetId: id as Id<'assetInventory'> }
  },
  component: AssetDetailRoute,
})

function AssetDetailRoute() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  
  // Use suspense query to read from cache (data already loaded in loader)
  const { data: asset } = useSuspenseQuery(
    convexQuery(api.assets.getAsset, { id: id as Id<'assetInventory'> })
  )
  
  // Handle asset deletion - Convex reactively updates to null when deleted
  // Redirect to assets list instead of throwing an error
  useEffect(() => {
    if (!asset) {
      navigate({ to: '/assets', replace: true })
    }
  }, [asset, navigate])
  
  // Show nothing while redirecting after deletion
  if (!asset) {
    return null
  }
  
  return <AssetDetailPage asset={asset} />
}
